// Stock for one variant in the other stores of this business unit's network, and
// the transfer request that pulls it here. Same services the Retail layout's
// stock drawer uses (getProductsNearbyStock / generateTransferRequest), without
// its placeholder "current store" row: the cashier already sees this store's
// stock in the size picker, so only other warehouses are listed.

export const NEARBY_STOCK_QUERY = `
  query NearbyStock($bUnitId: String, $code: String) {
    getProductsNearbyStock(bUnitId: $bUnitId, code: $code) {
      csBunitId
      totalQty
      stores {
        csNearbyStoreId
        name
        nearbybUnitId
        viewStock
        isIssue
        distanceKm
        warehouse {
          mWarehouseID
          name
          storageBin {
            totalQty
            stocks {
              qtyonhand
            }
          }
        }
      }
    }
  }
`;

export function buildNearbyStockRequest(tillData, code) {
  return { query: NEARBY_STOCK_QUERY, variables: { bUnitId: tillData?.tillAccess?.csBunit?.csBunitId, code: String(code || "") } };
}

const binQty = (bin) => {
  if (!bin) return 0;
  const total = Number(bin.totalQty);
  if (Number.isFinite(total)) return total;
  return (bin.stocks || []).reduce((n, s) => n + (Number(s?.qtyonhand) || 0), 0);
};

const km = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

// Stores with stock first, then the nearest (unknown distance last), then the most stock.
const byNearestStock = (a, b) =>
  (b.qty > 0) - (a.qty > 0) || (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity) || b.qty - a.qty || a.name.localeCompare(b.name);

// "4.2 km" under 10 km, whole km beyond; "" when the store has no map pin.
export function distanceLabel(distanceKm) {
  const d = km(distanceKm);
  if (d === null) return "";
  if (d < 1) return "Under 1 km";
  return d < 10 ? `${d.toFixed(1)} km` : `${Math.round(d)} km`;
}

// Response → [{ bunitId, name, warehouseId, qty, canIssue, distanceKm }], other
// stores only. Stores that don't share their stock (viewStock "N") are left out.
export function parseNearbyStores(response, tillData) {
  const stores = response?.data?.data?.getProductsNearbyStock?.stores;
  if (!Array.isArray(stores)) return null;
  const here = tillData?.tillAccess?.csBunit;
  return stores
    .filter((s) => s && s.viewStock !== "N")
    .filter((s) => s.warehouse?.mWarehouseID && s.warehouse.mWarehouseID !== here?.mWarehouse?.mWarehouseID && s.nearbybUnitId !== here?.csBunitId)
    .map((s) => ({
      bunitId: s.nearbybUnitId,
      name: s.name || s.warehouse.name || "Store",
      warehouseId: s.warehouse.mWarehouseID,
      qty: Math.max(0, binQty(s.warehouse.storageBin)),
      canIssue: s.isIssue === "Y",
      distanceKm: km(s.distanceKm),
    }))
    .sort(byNearestStock);
}

// The dataLoad argument is JSON inside a GraphQL string literal, so backslashes
// must be escaped before quotes (the Retail builder only escapes quotes).
export const toGraphQLString = (value) => JSON.stringify(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

export function buildTransferRequestMutation({ fromBunitId, toBunitId, lines, userId, description = "", requiredDate = "", now = new Date() }) {
  const stamp = now.toISOString().replace("T", " ").replace("Z", "");
  const dataLoad = [
    {
      fromBunitId,
      toBunitId,
      created: stamp,
      createdby: userId,
      updated: stamp,
      updatedby: userId,
      requiredDate: /^\d{4}-\d{2}-\d{2}$/.test(requiredDate) ? requiredDate : now.toISOString().split("T")[0],
      requestFor: "ST",
      description,
      lines: lines.map((l) => ({ productId: l.productId, qtyRequested: l.qty, pRequisitionLineId: null })),
    },
  ];
  return `mutation { generateTransferRequest(dataLoad: "${toGraphQLString(dataLoad)}") { message code recordId documentNo } }`;
}

// Returns an error message, or "" when the request may be sent.
export function transferQtyError(qty, store) {
  const n = Number(qty);
  if (!Number.isInteger(n) || n < 1) return "Enter a whole number of 1 or more.";
  if (!store?.canIssue) return `${store?.name || "This store"} can't send stock.`;
  if (n > store.qty) return `${store.name} has only ${store.qty}.`;
  return "";
}

export function readTransferResult(response) {
  const r = response?.data?.data?.generateTransferRequest;
  // documentNo is the plain list of document numbers; recordId is labelled and
  // starts with ", " when the server made no indent.
  const ref = r?.documentNo || String(r?.recordId || "").replace(/^[,\s]+/, "");
  if (r && String(r.code) === "200") return { ok: true, recordId: ref, message: r.message || "" };
  const err = response?.data?.errors?.[0]?.message;
  return { ok: false, message: plainTransferError(r?.message || err) || "The request didn't reach the server. Check the connection and try again." };
}

// The server can hand back a raw database error; the cashier gets a sentence
// they can act on instead.
export function plainTransferError(message) {
  const text = String(message || "").trim();
  if (!text) return "";
  if (/p_supplier_id|supplier/i.test(text) && /null|not-null/i.test(text)) {
    return "The stores aren't set up to supply each other yet, so the request wasn't sent. Ask head office to set up transfers between stores.";
  }
  if (/exception|sql|violates|ERROR:|INSERT INTO|SELECT /i.test(text) || text.length > 200) {
    return "The server couldn't create the transfer request, so it wasn't sent. Try again, or ask head office if it keeps failing.";
  }
  return text;
}

// Store × size grid for one colour: rows of { store, cells: { [size]: qty } }.
export function buildStockGrid(perSize) {
  const byStore = new Map();
  for (const { size, stores } of perSize) {
    for (const s of stores || []) {
      if (!byStore.has(s.bunitId)) byStore.set(s.bunitId, { store: { ...s, qty: 0 }, cells: {}, stores: {} });
      const row = byStore.get(s.bunitId);
      row.cells[size] = s.qty;
      row.stores[size] = s;
      row.store.qty += s.qty;
    }
  }
  return [...byStore.values()].sort((a, b) => byNearestStock(a.store, b.store));
}

// Last-synced on-hand quantity for this store, or null when the till doesn't track it.
export function localStockLabel(product) {
  const raw = product?.onhandQty;
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n > 0 ? `${n} here` : "Out here";
}

// Pieces already promised in each store: open holds and accepted online orders
// (getFashionStockCommitments). Free stock is on hand less both.
const cleanId = (s) => String(s ?? "").replace(/["\\]/g, "");
export function buildStockCommitmentsQuery(productIds) {
  const ids = [...new Set((productIds || []).filter(Boolean).map(cleanId))];
  return { query: `query { getFashionStockCommitments(productIds: [${ids.map((id) => `"${id}"`).join(", ")}]) { bunitId productId held reserved } }` };
}

// Response → { "bunitId|productId": { held, reserved } }, or null when the call failed.
export function readStockCommitments(response) {
  if (!response || response?.data?.errors?.length) return null;
  const rows = response?.data?.data?.getFashionStockCommitments;
  if (!Array.isArray(rows)) return null;
  const out = {};
  for (const r of rows) {
    if (!r?.bunitId || !r?.productId) continue;
    out[`${r.bunitId}|${r.productId}`] = { held: Math.max(0, Number(r.held) || 0), reserved: Math.max(0, Number(r.reserved) || 0) };
  }
  return out;
}

// On hand at a store less what is promised there; never below zero.
export function freeStock(onHand, commitments, bunitId, productId) {
  const c = commitments?.[`${bunitId}|${productId}`] || { held: 0, reserved: 0 };
  const promised = c.held + c.reserved;
  return { onHand, free: Math.max(0, onHand - promised), promised };
}

// Pieces of one variant across the other stores parseNearbyStores listed, or
// null when the lookup failed.
export function nearbyTotal(stores) {
  if (!Array.isArray(stores)) return null;
  return stores.reduce((n, s) => n + (Number(s?.qty) || 0), 0);
}

// How a piece from another store reaches the customer: sent here for pickup,
// shipped by that store to the customer's address, or held there for them.
export const TRANSFER_ROUTES = [
  { value: "here", label: "Transfer here" },
  { value: "ship", label: "Ship to home" },
  { value: "reserve", label: "Reserve there" },
];

const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// "Ready by" choices for a transfer: the next three days.
export function pickupDays(now = new Date()) {
  return [1, 2, 3].map((n) => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + n);
    return { value: isoDay(d), label: n === 1 ? "Tomorrow" : d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" }) };
  });
}

export const MAX_TRANSFER_NOTE = 255;

// The request's description, which the sending store reads: what it is for,
// who it is for, and where it goes.
export function transferNote({ route, itemName, customerName = "", mobileNo = "", shipTo = "", here = "" }) {
  const who = [customerName, mobileNo].map((v) => String(v || "").trim()).filter(Boolean).join(", ");
  const parts = [`Fashion POS request: ${itemName}`];
  if (route === "ship") parts.push(`Ship to the customer: ${shipTo}`);
  else if (who) parts.push(`For ${who}${here ? ` at ${here}` : ""}`);
  return parts.join(" · ").slice(0, MAX_TRANSFER_NOTE);
}
